import * as React from 'react'
import { observer } from 'mobx-react'
import { Container } from 'typescript-ioc/es5'

import Icon, {
    ApartmentOutlined,
    DeploymentUnitOutlined,
    DollarOutlined,
    FileSearchOutlined,
    FlagOutlined,
    GlobalOutlined,
    LockFilled,
    LogoutOutlined,
    MessageOutlined,
    TagFilled,
    TeamOutlined,
} from '@ant-design/icons'

import { Menu } from 'antd'
import { Sidebar as AqeSB } from '@pushly/aqe/lib/components'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { preventDefault } from '@pushly/aqe/lib/utils/prevent'
import { AuthService } from '@halo/services/auth.service'
import { PushlyIcon } from '@halo/components/icons/pushly-icon'
import { classnames } from '@halo/_utils/classnames'
import { MenuInfo } from 'rc-menu/lib/interface'
import aqe from '@pushly/aqe'
import { AccessRoleId } from '@halo/enums/access-role.enum'

const UserMenu = AqeSB.UserMenu

interface IMenuItem {
    key: string
    path?: string
    external?: boolean
    icon: React.ReactElement
    title: string
    condition?: () => boolean
    sub?: IMenuItem[]
}

@observer
export class Sidebar extends React.Component {
    protected appState: AppState
    protected appService: AppService
    protected authService: AuthService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.authService = Container.get(AuthService)
    }

    public render() {
        return (
            <AqeSB
                collapsed={this.appState.sidebar.collapsed}
                onToggle={(collapsed) => (collapsed ? this.appService.closeSidebar() : this.appService.openSidebar())}
            >
                <AqeSB.Header>
                    {/* TODO: Update AqeSB.AppIcon component */}
                    <div className={classnames('app-icon')}>
                        <span
                            onClick={() => {
                                this.appService.route('/')
                            }}
                        >
                            <Icon component={PushlyIcon} />
                        </span>
                    </div>
                    {/*<AqeSB.AppIcon*/}
                    {/*    onClick={() => {*/}
                    {/*        this.appService.route('/');*/}
                    {/*    }}*/}
                    {/*/>*/}
                </AqeSB.Header>
                <AqeSB.Content>
                    <Menu
                        mode="inline"
                        inlineCollapsed={this.appState.sidebar.collapsed}
                        subMenuCloseDelay={0}
                        onClick={this.handleMenuClick}
                        selectedKeys={this.currentlySelectedKeys}
                    >
                        {this.routes
                            .filter((r) => !r.condition || r.condition())
                            .map((r) =>
                                r.sub ? (
                                    <Menu.SubMenu key={r.key} title={r.title}>
                                        {r.sub.map((sr) => (
                                            <Menu.Item
                                                key={sr.key}
                                                data-path={sr.path}
                                                data-external={sr.external === true}
                                            >
                                                {sr.icon}
                                                <span>{sr.title}</span>
                                            </Menu.Item>
                                        ))}
                                    </Menu.SubMenu>
                                ) : (
                                    <Menu.Item key={r.key} data-path={r.path} data-external={r.external === true}>
                                        {r.icon}
                                        <span>{r.title}</span>
                                    </Menu.Item>
                                ),
                            )}
                    </Menu>
                </AqeSB.Content>
                <AqeSB.Footer>
                    {!!this.appState.currentUser && (
                        <UserMenu user={this.appState.currentUser}>
                            <UserMenu.Action>
                                <a onClick={this.handleLogout}>
                                    <LogoutOutlined />
                                    Logout
                                </a>
                            </UserMenu.Action>
                        </UserMenu>
                    )}
                </AqeSB.Footer>
            </AqeSB>
        )
    }

    protected get routes(): IMenuItem[] {
        return [
            {
                key: 'orgs',
                path: '/organizations',
                icon: <ApartmentOutlined />,
                title: 'Organizations',
                condition: this.appService.currentUserCanReadAtLeastOneOrg,
            },
            {
                key: 'domains',
                path: '/domains',
                icon: <GlobalOutlined />,
                title: 'Domains',
                condition: this.isActingUserInternal,
            },
            {
                key: 'users',
                path: '/users',
                icon: <TeamOutlined />,
                title: 'Users',
                condition: this.isActingUserInternal,
            },
            {
                key: 'batch-billing',
                path: '/batch-billing',
                icon: <DollarOutlined />,
                title: 'Batch Billing',
                condition: this.currentUserCanAccessBatchBilling,
            },
            {
                key: 'page_analysis_configs',
                path: '/page-analysis-configs',
                icon: <FileSearchOutlined />,
                title: 'Page Analysis Configs',
                condition: this.isActingUserInternalDev,
            },
            {
                key: 'jobs',
                path: '/dev/jobs',
                icon: <DeploymentUnitOutlined />,
                title: 'Jobs',
                condition: this.isActingUserInternalDev,
            },
            {
                key: 'flags',
                path: '/flags',
                icon: <FlagOutlined />,
                title: 'Flags',
                condition: this.isActingUserInternalDev,
            },
            {
                key: 'access-control',
                path: '/access-control',
                icon: <LockFilled />,
                title: 'Access Control',
                condition: this.isActingUserInternalDev,
            },
            {
                key: 'white-labels',
                path: '/white-labels',
                icon: <TagFilled />,
                title: 'White-Labels',
                condition: this.isActingUserInternalDev,
            },
            {
                key: 'messages',
                path: '/platform-messages',
                icon: <MessageOutlined />,
                title: 'Messages',
                condition: this.currentUserCanAccessPlatformMessages,
            },
            // Last item should always be Public Platform Link
            {
                key: 'pub-plat',
                path: aqe.defaults.publicPlatformDomain,
                external: true,
                icon: <Icon component={PushlyIcon} />,
                title: 'Public Platform',
            },
        ]
    }

    protected get currentlySelectedKeys(): string[] {
        const currentPath = this.appService.currentRoute
        const routes = this.routes
        const openRoutes = routes.filter(
            (r) => r.path === currentPath || (r.path !== '/' && new RegExp(`^${r.path}`, 'i').test(currentPath)),
        )

        return openRoutes.map((r) => r.key)
    }

    protected isActingUserInternal = (): boolean => {
        return this.appState.currentUser?.isInternalUser ?? false
    }

    protected isActingUserInternalDev = (): boolean => {
        return this.appState.currentUser?.isInternalDevUser ?? false
    }

    protected currentUserCanAccessBatchBilling = () => {
        return (
            this.isActingUserInternal &&
            [
                AccessRoleId.INTERNAL_DEVELOPER,
                AccessRoleId.INTERNAL_ACCOUNT_MANAGER_LEAD,
                AccessRoleId.INTERNAL_ACCOUNT_MANAGER,
            ].includes(this.appState.currentUser?.user_role_id)
        )
    }

    protected currentUserCanAccessPlatformMessages = (): boolean => {
        return (
            this.isActingUserInternal &&
            [AccessRoleId.INTERNAL_DEVELOPER, AccessRoleId.INTERNAL_ACCOUNT_MANAGER_LEAD].includes(
                this.appState.currentUser?.user_role_id,
            )
        )
    }

    protected handleMenuClick = async ({ item, domEvent }: MenuInfo) => {
        preventDefault(domEvent)

        const currentPath = this.appService.currentRoute
        const newPath = (item as any).node.dataset.path
        const isExternal = (item as any).node.dataset.external.toString().toLowerCase() === 'true'

        if ((domEvent as any).metaKey) {
            window.open(newPath)
        } else if (isExternal) {
            window.open(newPath, newPath)
        } else if (currentPath !== newPath) {
            this.appService.route(newPath)
        }
    }

    protected handleLogout = async (ev: any) => {
        preventDefault(ev)
        return this.authService.logout()
    }
}
